import logo from "assets/images/logoSvg.svg";
import "../assets/style.css";
import { SignUpForm } from "../components/SignUpForm";
import { useEffect, useState } from "react";
import { trackMetaSignupPageView } from "../../../utils/metaPixelJob";
import { captureCampaignFromUrl } from "../../../utils/campaignAttribution";
import { useIsAuthenticated } from "react-auth-kit";
import { Navigate, useParams, Link, useLocation } from "react-router-dom";
import { routePath } from "../../../config/routeMgt/routePath";
import { CloseOutlined } from "@ant-design/icons";

export const SignUp = () => {
  const isAuthenticated = useIsAuthenticated();
  const { type } = useParams();
  const location = useLocation();
  const [showCookieBanner, setShowCookieBanner] = useState(false);

  const accountType =
    type === "recruiter" || type === "talent" ? type : "talent";

  useEffect(() => {
    trackMetaSignupPageView();
  }, []);

  useEffect(() => {
    captureCampaignFromUrl(location.search);
  }, [location.search]);

  useEffect(() => {
    console.log("Checking cookie consent...");
    const consent = localStorage.getItem("cookieConsent");
    console.log("Cookie consent value:", consent);

    if (!consent) {
      console.log("No consent found, showing banner");
      setShowCookieBanner(true);
    } else {
      console.log("Consent found, hiding banner");
      setShowCookieBanner(false);
    }
  }, []);

  if (isAuthenticated()) {
    return <Navigate to={routePath.home} replace={true} />;
  }

  const handleCookieAccept = () => {
    console.log("Accepting cookies");
    localStorage.setItem("cookieConsent", "accepted");
    setShowCookieBanner(false);
  };

  const handleCookieOptOut = () => {
    console.log("Opting out of cookies");
    localStorage.setItem("cookieConsent", "opted-out");
    setShowCookieBanner(false);
  };

  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-2 h-screen overflow-auto">
        {/* LEFT SIDE */}
        <div className="Container py-3 bg-white flex flex-col h-full">
          <div className="mt-2">
            <a
              href="https://www.arnopro.com/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img src={logo} alt="logo" />
            </a>
          </div>

          <div className="flex justify-center mt-10 flex-1">
            <div className="w-full max-w-md">
              {/* Badge */}
              <div className="mb-4">
                <span className="inline-flex items-center gap-2 text-sm bg-gray-100 text-gray-700 px-3 py-1.5 rounded-full">
                  {accountType === "recruiter" ? "💼 " : "⭐ "}
                  Signing up as{" "}
                  {accountType === "recruiter" ? "Recruiter" : "Talent"}
                </span>
              </div>

              {/* FORM */}
              <SignUpForm
                selectedOptionProp={accountType}
                setStepProp={() => {}}
              />

              <div className="text-center text-sm mt-6">
                <span className="text-gray-500">Already have an account? </span>
                <Link
                  to={`/sign-in/${accountType}`}
                  className="text-primary font-medium"
                >
                  Sign in as{" "}
                  {accountType === "recruiter" ? "Recruiter" : "Talent"}
                </Link>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="text-sm flex items-center justify-center gap-16 pt-12 mt-auto pb-12">
            <a
              href="https://www.arnopro.com/arno-terms-of-use"
              className="underline"
              target="_blank"
              rel="noreferrer"
            >
              Terms & Conditions
            </a>
            <a
              href="https://www.arnopro.com/arno-data-privacy-policy"
              className="underline"
              target="_blank"
              rel="noreferrer"
            >
              Privacy Policy
            </a>
            <a
              href="https://www.arnopro.com/arno-cookies-policy"
              className="underline"
              target="_blank"
              rel="noreferrer"
            >
              Cookies Policy
            </a>
          </div>
        </div>

        <div className="bg-primary h-screen Container lg:flex items-center justify-center hidden w-full sticky top-0">
          <video
            src="/assets/authVideo.mp4"
            autoPlay
            loop
            muted
            playsInline
            className="w-full h-full object-cover"
          />
        </div>
      </div>

      {showCookieBanner && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/30 backdrop-blur-sm">
          <div className="w-full max-w-4xl mx-4 mb-8 animate-slide-up">
            <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 p-6 sm:p-8">
              <div className="flex flex-col md:flex-row items-start md:items-center gap-6">

                <div className="flex-shrink-0 hidden sm:flex items-center justify-center w-14 h-14 bg-blue-50 rounded-full">
                  <svg
                    className="w-8 h-8 text-blue-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z"
                    />
                  </svg>
                </div>

                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">
                    🍪 Cookie Preferences
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    We use cookies to enhance your browsing experience, analyze
                    site traffic, and personalize content. By clicking "Accept
                    All", you consent to our use of cookies in accordance with
                    our{" "}
                    <a
                      href="https://www.arnopro.com/arno-terms-of-use"
                      className="text-primary font-medium hover:underline"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Terms & Conditions
                    </a>
                    ,{" "}
                    <a
                      href="https://www.arnopro.com/arno-data-privacy-policy"
                      className="text-primary font-medium hover:underline"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Privacy Policy
                    </a>
                    , and{" "}
                    <a
                      href="https://www.arnopro.com/arno-cookies-policy"
                      className="text-primary font-medium hover:underline"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Cookies Policy
                    </a>
                    . You can manage your preferences at any time.
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto flex-shrink-0">
                  <button
                    onClick={handleCookieAccept}
                    className="px-8 py-2.5 bg-primary text-white rounded-lg font-medium hover:bg-primary-dark transition-colors shadow-sm hover:shadow-md whitespace-nowrap"
                  >
                    Accept All
                  </button>
                  <button
                    onClick={handleCookieOptOut}
                    className="px-6 py-2.5 border border-gray-300 rounded-lg font-medium hover:bg-gray-50 transition-colors whitespace-nowrap"
                  >
                    Opt Out
                  </button>
                </div>

                <button
                  onClick={handleCookieOptOut}
                  className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors md:hidden"
                >
                  <CloseOutlined className="text-lg" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        .animate-slide-up {
          animation: slideUp 0.4s ease-out forwards;
        }
      `}</style>
    </>
  );
};
